import React, { useEffect, useMemo, useState } from 'react';
import API from '../services/api';
import { useNavigate } from 'react-router-dom';
import DiagnosticWizard from '../components/DiagnosticWizard';
import './Diagnostic.css';
import { getUser } from '../utils/authUtil';

const DiagnosticAnalysis = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [batch, setBatch] = useState('all');
  const [branch, setBranch] = useState('all');
  const [readiness, setReadiness] = useState('all');
  const [college, setCollege] = useState('all');
  const [activeTab, setActiveTab] = useState('reports');
  const [showWizard, setShowWizard] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState(null);

  const [createForm, setCreateForm] = useState({
    test_name: '',
    duration_minutes: 30,
    total_marks: 10
  });
  const [createMsg, setCreateMsg] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  const user = getUser();
  const isSuperAdmin = user?.realRole === 'super_admin';

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const res = await API.get('/diagnostic/reports');
      setStudents(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDiagnostic = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateMsg('');

    try {
      const res = await API.post('/tests', {
        test_name: createForm.test_name,
        test_type: 'Diagnostic Test',
        duration_minutes: parseInt(createForm.duration_minutes) || 30,
        total_marks: parseInt(createForm.total_marks) || 10,
        negative_marking: false
      });

      setCreateMsg(`✅ Success! Initializing wizard...`);
      setSelectedTestId(res.data.test.id);
      setShowWizard(true);
      setCreateForm({ test_name: '', duration_minutes: 30, total_marks: 10 });
    } catch (err) {
      setCreateMsg(`❌ ${err.response?.data?.message || 'Failed to create test'}`);
    } finally {
      setCreateLoading(false);
    }
  };

  // Derive filter options from the real fetched data (no hardcoded lists).
  const batches = useMemo(
    () => [...new Set(students.map(s => s.placement_batch_year).filter(Boolean))].sort((a, b) => b - a),
    [students]
  );
  const branches = useMemo(
    () => [...new Set(students.map(s => s.branch).filter(Boolean))].sort(),
    [students]
  );
  const readinessLevels = useMemo(
    () => [...new Set(students.map(s => s.readiness_level).filter(Boolean))].sort(),
    [students]
  );
  const colleges = useMemo(
    () => [...new Set(students.map(s => s.college_name).filter(Boolean))].sort(),
    [students]
  );

  const filtered = useMemo(() => students.filter(s => {
    if (search) {
      const q = search.toLowerCase();
      if (!(s.name?.toLowerCase().includes(q) ||
            s.roll_number?.toLowerCase().includes(q) ||
            s.college_name?.toLowerCase().includes(q))) return false;
    }
    if (batch !== 'all' && String(s.placement_batch_year) !== String(batch)) return false;
    if (branch !== 'all' && s.branch !== branch) return false;
    if (readiness !== 'all' && s.readiness_level !== readiness) return false;
    if (college !== 'all' && s.college_name !== college) return false;
    return true;
  }), [students, search, batch, branch, readiness, college]);

  // Super admin: group the filtered students by college for a separated view.
  const groupedByCollege = useMemo(() => {
    const groups = new Map();
    filtered.forEach(s => {
      const key = s.college_name || 'Unassigned College';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(s);
    });
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  // Presentational-only: map a readiness_level string to a badge colour variant.
  const readinessVariant = (level) => {
    const l = (level || '').toLowerCase();
    if (l.includes('top')) return 'top';
    if (l.includes('ready')) return 'ready';
    if (l.includes('moderate')) return 'moderate';
    if (l.includes('need')) return 'needs';
    return 'neutral';
  };

  const renderStudentCard = (s) => {
    const dateObj = new Date(s.completed_at);
    const formattedDate = !isNaN(dateObj) ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase() : (s.completed_at ? s.completed_at : 'OCT 24, 2023');
    const badgeText = s.last_test_title ? s.last_test_title.substring(0, 8) : 'TEST A';

    return (
      <div key={s.id} className="diag-student-card" onClick={() => navigate(`/admin/diagnostic/report/${s.attempt_id}`)}>
        <div className="dsc-avatar">{s.name ? s.name.charAt(0) : '?'}</div>

        <div className="dsc-content-wrapper">
          <h3 className="dsc-name">{s.name}</h3>
          <div className="dsc-content-mid">
            <span className="dsc-icon">🎓</span>
            <span className="dsc-reg">{s.roll_number} | {s.branch}</span>
          </div>
          <div className="dsc-content-bottom">
            <span className="dsc-test-badge">{badgeText}</span>
            <span className="dsc-date">{formattedDate}</span>
          </div>
        </div>

        <div className="dsc-metrics">
          {s.readiness_level && (
            <span className={`dsc-status-badge ${readinessVariant(s.readiness_level)}`}>
              {s.readiness_level}
            </span>
          )}
          {s.eri_score !== undefined && (
            <div className="dsc-eri">
              <span className="dsc-eri-label">ERI</span>
              <span className="dsc-eri-value">{parseFloat(s.eri_score).toFixed(0)}</span>
            </div>
          )}
        </div>

        <button type="button" className="dsc-action" tabIndex={-1}>
          View Report
          <span className="dsc-action-arrow">➔</span>
        </button>
      </div>
    );
  };

  if (showWizard && selectedTestId) {
    return (
      <div className="diagnostic-wizard-modal">
        <DiagnosticWizard
          testId={selectedTestId}
          onComplete={() => { setShowWizard(false); fetchStudents(); setActiveTab('reports'); }}
          onCancel={() => { setShowWizard(false); setSelectedTestId(null); }}
        />
      </div>
    );
  }

  return (
    <div className="diagnostic-page-content">
      <div className="diagnostic-page-wrapper">
        <header className="bento-header diag-mgmt-toolbar">
          <div className="b-meta">
            <h1 className="premium-title">Diagnostic Analysis</h1>
            <p className="premium-subtitle">
              {isSuperAdmin ? 'Centralized cognitive diagnostic management' : 'Placement readiness & skill analysis dashboard'}
            </p>
          </div>
          <div className="diag-toolbar-actions">
            {activeTab === 'reports' && (
              <div className="diag-search-wrap">
                <svg className="diag-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  placeholder="Search students, roll numbers, or colleges..."
                  className="diag-search-input"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            )}
            {isSuperAdmin && activeTab === 'reports' && (
              <button className="btn-submit-premium" style={{ width: 'auto', padding: '12px 24px' }} onClick={() => setActiveTab('create')}>
                <span>+</span> Create New Scan
              </button>
            )}
          </div>
        </header>

        <nav className="tabs-container">
          <button className={`tab-link ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => setActiveTab('reports')}>
            Student Reports
          </button>
          {isSuperAdmin && (
            <button className={`tab-link ${activeTab === 'create' ? 'active' : ''}`} onClick={() => setActiveTab('create')}>
              Assessment Engine
            </button>
          )}
        </nav>

        <main className="tab-content">
          {activeTab === 'reports' ? (
            <div className="reports-view">
              {!loading && students.length > 0 && (
                <div className="diag-filters">
                  <select className="diag-filter-select" value={batch} onChange={e => setBatch(e.target.value)}>
                    <option value="all">All Batches</option>
                    {batches.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                  <select className="diag-filter-select" value={branch} onChange={e => setBranch(e.target.value)}>
                    <option value="all">All Branches</option>
                    {branches.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                  <select className="diag-filter-select" value={readiness} onChange={e => setReadiness(e.target.value)}>
                    <option value="all">All Readiness</option>
                    {readinessLevels.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  {isSuperAdmin && (
                    <select className="diag-filter-select" value={college} onChange={e => setCollege(e.target.value)}>
                      <option value="all">All Colleges</option>
                      {colleges.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  )}
                  <span className="diag-filter-count">{filtered.length} student{filtered.length !== 1 ? 's' : ''}</span>
                </div>
              )}

              {loading ? (
                <div className="loading-state">✨ Fetching student performance records...</div>
              ) : filtered.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">🔬</div>
                  <h3>No diagnostic records</h3>
                  <p>Assign a diagnostic test to see real-time skill mapping.</p>
                </div>
              ) : isSuperAdmin ? (
                <div className="diag-college-groups">
                  {groupedByCollege.map(([collegeName, list]) => (
                    <section key={collegeName} className="diag-college-group">
                      <div className="diag-college-header">
                        <span className="diag-college-name">🏫 {collegeName}</span>
                        <span className="diag-college-count">{list.length} student{list.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="diag-student-grid">
                        {list.map(renderStudentCard)}
                      </div>
                    </section>
                  ))}
                </div>
              ) : (
                <div className="diag-student-grid">
                  {filtered.map(renderStudentCard)}
                </div>
              )}
            </div>
          ) : (
            <div className="create-section-wrapper">
              <div className="create-card-premium">
                <div className="create-card-header">
                  <h3>Configure Diagnostic Scan</h3>
                  <p>Setup a new automated diagnostic test for student mapping.</p>
                </div>
                <form onSubmit={handleCreateDiagnostic} className="premium-form">
                  <div className="form-group-p">
                    <label>Assesment Title</label>
                    <input type="text" value={createForm.test_name}
                      onChange={e => setCreateForm({ ...createForm, test_name: e.target.value })}
                      placeholder="e.g., Engineering Aptitude Level II" required />
                  </div>
                  <div className="form-row-p">
                    <div className="form-group-p">
                      <label>Duration (mins)</label>
                      <input type="number" value={createForm.duration_minutes}
                        onChange={e => setCreateForm({ ...createForm, duration_minutes: e.target.value })} />
                    </div>
                    <div className="form-group-p">
                      <label>Target Qs</label>
                      <input type="number" value={createForm.total_marks}
                        onChange={e => setCreateForm({ ...createForm, total_marks: e.target.value })} />
                    </div>
                  </div>
                  <button type="submit" disabled={createLoading} className="btn-submit-premium">
                    {createLoading ? '⏳ Initializing Scan...' : '🚀 Create & Assign'}
                  </button>
                  {createMsg && <div className={`msg-box-p ${createMsg.includes('❌') ? 'error' : 'success'}`}>{createMsg}</div>}
                </form>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default DiagnosticAnalysis;
